import { cn } from '@lib/utils';
import Image from 'next/image';
import { TrackedButton } from '@/components/button';

const MOBILE_VISIBLE_PAGE_COUNT = 7;
const PREV = 'prev';
const NEXT = 'next';
const ELLIPSIS = '...';

type PageItem = typeof NEXT | typeof PREV | typeof ELLIPSIS | number;

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  onPageClick: (newPage: number) => void;
  onPrevClick: () => void;
  onNextClick: () => void;
  icons?: {
    prevIcon: string;
    nextIcon: string;
    ellipsisIcon: string;
  };
  className?: string;
  visiblePageCount?: number;
}

function getPageLabel(
  page: PageItem,
  icons: {
    prevIcon: string;
    nextIcon: string;
    ellipsisIcon: string;
  },
) {
  switch (page) {
    case PREV:
      return (
        <Image
          src={icons.prevIcon}
          alt="이전 페이지"
          width={32}
          height={32}
          draggable={false}
        />
      );
    case NEXT:
      return (
        <Image
          src={icons.nextIcon}
          alt="다음 페이지"
          width={32}
          height={32}
          draggable={false}
        />
      );
    case ELLIPSIS:
      return (
        <Image
          src={icons.ellipsisIcon}
          alt="더 많은 페이지"
          width={16}
          height={16}
          draggable={false}
        />
      );
    default:
      return <span>{page}</span>;
  }
}

/**
 * 페이지네이션에 표시할 페이지 배열을 생성하는 함수들
 */
function generateSimplePageArray(totalPages: number): PageItem[] {
  return Array.from({ length: totalPages }, (_, i) => i + 1);
}

function generateStartPageArray(visiblePageCount: number): PageItem[] {
  const pages: PageItem[] = [];

  for (let i = 1; i <= visiblePageCount - 2; i += 1) {
    pages.push(i);
  }

  pages.push(ELLIPSIS);
  return pages;
}

function generateEndPageArray(
  totalPages: number,
  visiblePageCount: number,
): PageItem[] {
  const pages: PageItem[] = [1, ELLIPSIS];

  for (let i = totalPages - (visiblePageCount - 3); i <= totalPages; i += 1) {
    if (i > 1) pages.push(i);
  }

  return pages;
}

function generateMiddlePageArray(
  currentPage: number,
  totalPages: number,
  visiblePageCount: number,
): PageItem[] {
  const pages: PageItem[] = [1, ELLIPSIS];
  const offsetFromCenter = Math.floor((visiblePageCount - 5) / 2);

  for (
    let i = currentPage - offsetFromCenter;
    i <= currentPage + offsetFromCenter;
    i += 1
  ) {
    if (i > 1 && i < totalPages) {
      pages.push(i);
    }
  }

  pages.push(ELLIPSIS);
  return pages;
}

/**
 * 현재 페이지와 전체 페이지 수를 기반으로 표시할 페이지 배열을 생성합니다.
 * 페이지 위치(시작, 중간, 끝)에 따라 다른 전략을 사용하여 페이지 배열을 구성합니다.
 */
function generatePageArray(
  currentPage: number,
  totalPages: number,
  visiblePageCount: number = MOBILE_VISIBLE_PAGE_COUNT,
): PageItem[] {
  // 1. 기본 페이지 배열 생성
  let pageItems: PageItem[] = [];
  const isSimpleView = totalPages <= visiblePageCount;

  if (isSimpleView) {
    // 전체 페이지 수가 표시 가능한 개수보다 작으면 모든 페이지 표시
    pageItems = generateSimplePageArray(totalPages);
  } else {
    // 현재 페이지 위치에 따른 전략 선택
    const isNearStart = currentPage <= Math.ceil(visiblePageCount / 2);
    const isNearEnd =
      currentPage > totalPages - Math.ceil(visiblePageCount / 2);

    if (isNearStart) {
      pageItems = generateStartPageArray(visiblePageCount);
    } else if (isNearEnd) {
      pageItems = generateEndPageArray(totalPages, visiblePageCount);
    } else {
      pageItems = generateMiddlePageArray(
        currentPage,
        totalPages,
        visiblePageCount,
      );
    }

    // last ellipsis 뒤에 마지막 페이지가 없는 경우 추가
    if (pageItems[pageItems.length - 1] !== totalPages) {
      pageItems.push(totalPages);
    }
  }

  return [PREV, ...pageItems, NEXT];
}

function getAriaLabel(page: PageItem): string {
  if (page === PREV) return '이전 페이지';
  if (page === NEXT) return '다음 페이지';
  if (page === ELLIPSIS) return '더 많은 페이지';
  return `${page} 페이지로 이동`;
}

function Pagination({
  currentPage,
  totalPages,
  onPageClick,
  onPrevClick,
  onNextClick,
  icons,
  className,
  visiblePageCount = MOBILE_VISIBLE_PAGE_COUNT,
}: PaginationProps) {
  const defaultIcons = {
    prevIcon: `/images/chevron-left${currentPage === 1 ? '-disabled' : ''}.svg`,
    nextIcon: `/images/chevron-right${currentPage === totalPages ? '-disabled' : ''}.svg`,
    ellipsisIcon: '/images/ellipsis.svg',
  };

  // 페이지 클릭 이벤트 핸들러
  const handlePageClick = (page: PageItem) => {
    if (page === PREV) {
      if (currentPage > 1) {
        onPrevClick();
      }
      return;
    }

    if (page === NEXT) {
      if (currentPage < totalPages) {
        onNextClick();
      }
      return;
    }

    if (page !== ELLIPSIS) {
      // 현재 페이지와 동일한 경우 불필요한 호출 방지
      if (page === currentPage) return;

      onPageClick(page);
    }
    // ellipsis 클릭 시 아무 동작 안함
  };

  const paginationIcons = icons || defaultIcons;
  const pages = generatePageArray(currentPage, totalPages, visiblePageCount);

  return (
    <ol className={cn('flex w-full justify-center', className)}>
      {pages.map((_page, index) => {
        const isCurrentPage = _page === currentPage;
        const isPrevDisabled = _page === PREV && currentPage <= 1;
        const isEllipsis = _page === ELLIPSIS;
        const isNextDisabled = _page === NEXT && currentPage >= totalPages;
        const isDisabled =
          isCurrentPage || isEllipsis || isPrevDisabled || isNextDisabled;

        const itemClassName = cn(
          'flex h-full w-full items-center justify-center text-caption font-semibold text-3b-text-secondary',
          isDisabled ? 'rounded-lg pointer-events-none' : '',
          isCurrentPage
            ? 'bg-3b-blue-lightAlpha-50 text-black'
            : 'hover:underline',
          isEllipsis ? 'bg-3b-background-medium' : '',
        );

        // '...'가 여러개 들어올 수 있으므로 key에 index 조합
        return (
          <li
            key={`${_page}-${index}`}
            className="flex h-10 w-10 items-center justify-center p-1"
          >
            <TrackedButton
              label={`pagination_${_page}`}
              position="main_content"
              type="button"
              onClick={() => !isDisabled && handlePageClick(_page)}
              className={itemClassName}
              tabIndex={isDisabled ? -1 : 0}
              disabled={isDisabled}
              aria-current={isCurrentPage ? 'page' : undefined}
              aria-label={getAriaLabel(_page)}
            >
              {getPageLabel(_page, paginationIcons)}
            </TrackedButton>
          </li>
        );
      })}
    </ol>
  );
}

export default Pagination;
